import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Children, createContext, isValidElement, useCallback, useContext, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore, } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';
import { Button, Popover, Separator } from '@wit-support/ui';
import { cn } from './lib/cn.js';
/**
 * Какие элементы помещаются в строку, а какие уходят в «Ещё ▾».
 *
 * Узкий режим (`narrow`, экран уже md): в меню уходит ВСЁ, что не влезло,
 * включая primary — иначе на телефоне primary налезают на «Ещё» и уезжают за
 * край строки. Место по порядку разметки занимают сначала primary, затем
 * secondary; не влезший уходит в меню, следующие ещё пробуют влезть.
 *
 * Обычный режим: primary в строке ВСЕГДА (в меню не уходят никогда, даже если
 * не помещаются).
 * secondary — если помещаются все, то все в строке; иначе под кнопку «Ещё»
 * резервируется место и secondary по порядку занимают остаток, пока влезают:
 * первый не влезший и все после него уходят в меню. Порядок в меню и в строке
 * — порядок разметки.
 */
export function fitToolbarItems(items, available, moreButtonWidth, opts = {}) {
    const sorted = [...items].sort((a, b) => a.order - b.order);
    if (opts.narrow) {
        const total = sorted.reduce((s, i) => s + i.width, 0);
        if (total <= available)
            return { inline: sorted.map((i) => i.id), overflow: [] };
        const budget = available - moreButtonWidth;
        const inlineIds = new Set();
        let acc = 0;
        const byPriority = [
            ...sorted.filter((i) => i.priority === 'primary'),
            ...sorted.filter((i) => i.priority === 'secondary'),
        ];
        for (const i of byPriority) {
            if (acc + i.width <= budget) {
                inlineIds.add(i.id);
                acc += i.width;
            }
        }
        return {
            inline: sorted.filter((i) => inlineIds.has(i.id)).map((i) => i.id),
            overflow: sorted.filter((i) => !inlineIds.has(i.id)).map((i) => i.id),
        };
    }
    const primary = sorted.filter((i) => i.priority === 'primary');
    const secondary = sorted.filter((i) => i.priority === 'secondary');
    const sum = (xs) => xs.reduce((s, i) => s + i.width, 0);
    const used = sum(primary);
    const inlineIds = new Set(primary.map((i) => i.id));
    const overflow = [];
    if (used + sum(secondary) <= available) {
        for (const i of secondary)
            inlineIds.add(i.id);
    }
    else {
        const budget = available - moreButtonWidth;
        let acc = used;
        let full = false;
        for (const i of secondary) {
            if (!full && acc + i.width <= budget) {
                inlineIds.add(i.id);
                acc += i.width;
            }
            else {
                full = true;
                overflow.push(i.id);
            }
        }
    }
    return { inline: sorted.filter((i) => inlineIds.has(i.id)).map((i) => i.id), overflow };
}
/**
 * Внешнее хранилище, а не state провайдера: регистрация элементов идёт из
 * layout-эффектов страницы, и перерисовываться должны только слот и сами
 * элементы, а не всё дерево маршрута.
 */
class ToolbarStore {
    owners = [];
    items = new Map();
    targets = new Map();
    version = 0;
    listeners = new Set();
    subscribe = (listener) => {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    };
    getVersion = () => this.version;
    emit() {
        this.version++;
        for (const l of [...this.listeners])
            l();
    }
    pushOwner(owner, label) {
        // Повторная регистрация (смена подписи) место в порядке монтирования не меняет.
        const at = this.owners.findIndex((o) => o.owner === owner);
        this.owners =
            at >= 0
                ? this.owners.map((o, i) => (i === at ? { owner, label } : o))
                : [...this.owners, { owner, label }];
        this.emit();
    }
    removeOwner(owner) {
        this.owners = this.owners.filter((o) => o.owner !== owner);
        this.emit();
    }
    ownerCount() {
        return this.owners.length;
    }
    /**
     * Тулбары одного экрана сливаются в один: подпись группы — у первого
     * смонтированного (у `ModuleScreen` это он сам, он стоит в разметке раньше).
     */
    active() {
        return this.owners[0] ?? null;
    }
    upsertItem(meta) {
        const cur = this.items.get(meta.id);
        if (cur &&
            cur.owner === meta.owner &&
            cur.kind === meta.kind &&
            cur.priority === meta.priority &&
            cur.label === meta.label &&
            cur.order === meta.order) {
            return;
        }
        this.items.set(meta.id, meta);
        this.emit();
    }
    removeItem(id) {
        if (this.items.delete(id))
            this.emit();
    }
    /** Зарегистрирован ли элемент (у любого тулбара, не только активного). */
    hasItem(id) {
        return this.items.has(id);
    }
    /**
     * Элементы всех тулбаров экрана: сперва по порядку монтирования тулбара
     * (`ModuleScreen` — первым), внутри тулбара — по порядку разметки.
     */
    activeItems() {
        const rank = (owner) => this.owners.findIndex((o) => o.owner === owner);
        return [...this.items.values()]
            .filter((i) => rank(i.owner) >= 0)
            .sort((a, b) => rank(a.owner) - rank(b.owner) || a.order - b.order);
    }
    setTarget(id, el) {
        if (this.targets.get(id) === el)
            return;
        this.targets.set(id, el);
        this.emit();
    }
    clearTarget(id, el) {
        if (this.targets.get(id) !== el)
            return;
        this.targets.delete(id);
        this.emit();
    }
    targetOf(id) {
        return this.targets.get(id);
    }
}
const NOOP_SUBSCRIBE = () => () => undefined;
const ZERO = () => 0;
function useStoreVersion(store) {
    return useSyncExternalStore(store?.subscribe ?? NOOP_SUBSCRIBE, store?.getVersion ?? ZERO, ZERO);
}
const ModeContext = createContext(null);
const StoreContext = createContext(null);
const SetDescriptionContext = createContext(null);
const DescriptionValueContext = createContext(null);
const RenderContext = createContext({ kind: 'inline' });
const OrderContext = createContext(0);
/** Оборачивает маршруты в оболочке. Режим меняется вместе с маршрутом. */
export function PageChromeProvider({ mode, children }) {
    const [store] = useState(() => new ToolbarStore());
    const [description, setDescription] = useState(null);
    return (_jsx(ModeContext.Provider, { value: mode, children: _jsx(StoreContext.Provider, { value: store, children: _jsx(SetDescriptionContext.Provider, { value: setDescription, children: _jsx(DescriptionValueContext.Provider, { value: description?.value ?? null, children: children }) }) }) }));
}
/** Описание текущего экрана (оболочка покажет его ⓘ у крошек) или `null`. */
export function usePageDescriptionValue() {
    const mode = useContext(ModeContext);
    const value = useContext(DescriptionValueContext);
    return mode === 'shell' ? value : null;
}
/** Ширина кнопки «Ещё» до первого замера, px. */
const MORE_BUTTON_FALLBACK = 80;
/** Промежуток между элементами строки (`gap-1.5`), px. */
const GAP = 6;
/** Разделитель с промежутком, px. */
const SEPARATOR_WIDTH = 1 + GAP;
function itemsWord(n) {
    const mod10 = n % 10;
    const mod100 = n % 100;
    if (mod10 === 1 && mod100 !== 11)
        return 'элемент';
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14))
        return 'элемента';
    return 'элементов';
}
/**
 * Сколько разделителей будет видно в строке: только между двумя элементами
 * строки, без повторов (тем же правилом строится `row` в слоте).
 */
function visibleSeparators(all, isInline) {
    let count = 0;
    let pending = false;
    let anyInline = false;
    for (const i of all) {
        if (i.kind === 'separator') {
            if (anyInline)
                pending = true;
            continue;
        }
        if (!isInline(i.id))
            continue;
        if (pending)
            count++;
        pending = false;
        anyInline = true;
    }
    return count;
}
/**
 * {@link fitToolbarItems} для строки с разделителями (по порядку разметки):
 * место вычитается только под разделители, которые могут оказаться видимыми, —
 * между двумя элементами, без ведущих, хвостовых и повторных. Разделитель у
 * элемента, ушедшего в «Ещё», тоже учтён, и это точно, а не с запасом: вернуть
 * такой элемент в строку можно, только показав и разделитель.
 */
export function fitToolbarRow(entries, available, moreButtonWidth, opts = {}) {
    const separators = visibleSeparators(entries, () => true);
    return fitToolbarItems(entries.filter((e) => e.kind === 'item'), available - separators * SEPARATOR_WIDTH, moreButtonWidth, opts);
}
/** Ширина, ниже которой тулбар в узком режиме (Tailwind `md`). */
export const TOOLBAR_NARROW_QUERY = '(max-width: 767.98px)';
/** Экран уже md; без `matchMedia` (jsdom) — нет. */
function useNarrow() {
    const mql = () => typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        ? window.matchMedia(TOOLBAR_NARROW_QUERY)
        : null;
    const [narrow, setNarrow] = useState(() => mql()?.matches ?? false);
    useEffect(() => {
        const m = mql();
        if (!m)
            return;
        const on = () => setNarrow(m.matches);
        on();
        m.addEventListener('change', on);
        return () => m.removeEventListener('change', on);
    }, []);
    return narrow;
}
/**
 * Место тулбара страницы в навбаре оболочки: DOM-цели порталов элементов +
 * замер ширины (`ResizeObserver`) + меню «Ещё ▾» для secondary, которые не
 * поместились. Пока тулбара нет, внутри пусто и слот скрыт (`:empty`) — не
 * занимает места и не добавляет вторую строку навбару.
 *
 * Минимальная ширина слота — сумма primary (+ «Ещё», если есть secondary)
 * в CSS-переменной `--page-toolbar-min`: оболочка ставит
 * `min-w-[var(--page-toolbar-min)]`, и соседи (крошки) сжимаются раньше, чем
 * primary начнут вылезать.
 */
export function PageToolbarSlot({ className, moreLabel = 'Ещё', }) {
    const store = useContext(StoreContext);
    useStoreVersion(store);
    const narrow = useNarrow();
    const [available, setAvailable] = useState(0);
    const widths = useRef(new Map());
    const [, setWidthsVersion] = useState(0);
    const [moreWidth, setMoreWidth] = useState(0);
    const observer = useRef(null);
    const rootRef = useRef(null);
    const refCache = useRef(new Map());
    useLayoutEffect(() => {
        if (typeof ResizeObserver === 'undefined')
            return;
        const ro = new ResizeObserver((entries) => {
            let changed = false;
            for (const e of entries) {
                const el = e.target;
                if (el === rootRef.current) {
                    setAvailable(el.clientWidth);
                    continue;
                }
                const id = el.dataset.toolbarItem;
                if (id) {
                    const w = el.getBoundingClientRect().width;
                    if (w > 0 && widths.current.get(id) !== w) {
                        widths.current.set(id, w);
                        changed = true;
                    }
                }
                else if (el.dataset.toolbarMore !== undefined) {
                    setMoreWidth(el.getBoundingClientRect().width);
                }
            }
            if (changed)
                setWidthsVersion((v) => v + 1);
        });
        observer.current = ro;
        const root = rootRef.current;
        if (root) {
            // Ref'ы первого коммита прикрепились раньше, чем создан наблюдатель.
            ro.observe(root);
            root
                .querySelectorAll('[data-toolbar-item],[data-toolbar-more]')
                .forEach((el) => ro.observe(el));
        }
        return () => {
            ro.disconnect();
            observer.current = null;
        };
    }, []);
    /** Стабильный callback ref на цель портала: `<where>:<id>`. */
    const targetRef = useCallback((where, id) => {
        const key = `${where}:${id}`;
        let fn = refCache.current.get(key);
        if (!fn) {
            let current = null;
            fn = (el) => {
                if (current) {
                    observer.current?.unobserve(current);
                    if (where !== 'more')
                        store?.clearTarget(id, current);
                }
                current = el;
                if (!el)
                    return;
                if (where === 'inline' || where === 'more')
                    observer.current?.observe(el);
                if (where !== 'more')
                    store?.setTarget(id, el);
            };
            refCache.current.set(key, fn);
        }
        return fn;
    }, [store]);
    // Уборка за снятыми элементами: замеры и ref'ы по `useId` иначе копились бы
    // весь рабочий день (каждый заход на экран — новые id). К этому моменту
    // React уже отцепил ref'ы удалённых элементов.
    useLayoutEffect(() => {
        if (!store)
            return;
        for (const id of widths.current.keys()) {
            if (!store.hasItem(id))
                widths.current.delete(id);
        }
        for (const key of refCache.current.keys()) {
            const id = key.slice(key.indexOf(':') + 1);
            if (key !== 'more:more' && !store.hasItem(id))
                refCache.current.delete(key);
        }
    });
    const setRoot = useCallback((el) => {
        if (rootRef.current && rootRef.current !== el)
            observer.current?.unobserve(rootRef.current);
        rootRef.current = el;
        if (el)
            observer.current?.observe(el);
    }, []);
    const active = store?.active() ?? null;
    const all = store?.activeItems() ?? [];
    const real = all.filter((i) => i.kind === 'item');
    // Пока ширина не измерена (первый кадр, jsdom) — всё в строке.
    const fit = fitToolbarRow(all.map((i, index) => ({
        id: i.id,
        kind: i.kind,
        priority: i.priority,
        order: index,
        width: i.kind === 'item' ? (widths.current.get(i.id) ?? 0) + GAP : 0,
    })), available > 0 ? available : Number.POSITIVE_INFINITY, (moreWidth || MORE_BUTTON_FALLBACK) + GAP, { narrow });
    const overflowSet = new Set(fit.overflow);
    const overflowItems = real.filter((i) => overflowSet.has(i.id));
    // Разделитель — только между двумя видимыми в строке элементами, без повторов.
    const row = [];
    let pendingSeparator = null;
    let anyInline = false;
    for (const i of all) {
        if (i.kind === 'separator') {
            if (anyInline)
                pendingSeparator = i.id;
            continue;
        }
        if (overflowSet.has(i.id))
            continue;
        if (pendingSeparator) {
            row.push(_jsx(Separator, { orientation: "vertical", className: "mx-0.5 h-5 self-center" }, pendingSeparator));
            pendingSeparator = null;
        }
        anyInline = true;
        row.push(_jsx("div", { ref: targetRef('inline', i.id), "data-toolbar-item": i.id, role: "group", "aria-label": i.label, className: "flex shrink-0 items-center gap-1.5" }, `inline:${i.id}`));
    }
    const primaryWidth = real
        .filter((i) => i.priority === 'primary')
        .reduce((s, i) => s + (widths.current.get(i.id) ?? 0) + GAP, 0);
    // Место под «Ещё» резервируется при ЛЮБЫХ secondary, а не только при
    // переполнении: иначе появление кнопки расширяет слот, расширенный слот
    // вмещает всё, кнопка пропадает, слот сужается — и так по кругу.
    const hasSecondary = real.some((i) => i.priority === 'secondary');
    const moreW = (moreWidth || MORE_BUTTON_FALLBACK) + GAP;
    // Узкий экран: primary тоже уходят в «Ещё», поэтому слоту хватает места под
    // одну кнопку «Ещё» — он не распирает навбар.
    const minWidth = narrow ? moreW : primaryWidth + (hasSecondary ? moreW : 0);
    return (_jsx("div", { ref: setRoot, role: active ? 'group' : undefined, "aria-label": active?.label, "data-page-toolbar-slot": "", style: { '--page-toolbar-min': `${Math.ceil(minWidth)}px` }, className: cn('flex min-w-0 items-center justify-end gap-1.5 [&:empty]:hidden', className), children: active && real.length > 0 ? (_jsxs(_Fragment, { children: [row, overflowItems.length > 0 ? (_jsxs(Popover, { children: [_jsx(Popover.Trigger, { asChild: true, children: _jsxs(Button, { ref: targetRef('more', 'more'), "data-toolbar-more": "", variant: "ghost", size: "sm", className: "sticky right-0 shrink-0 gap-1 bg-[var(--color-surface-canvas)]", "aria-label": `${moreLabel}: ${overflowItems.length} ${itemsWord(overflowItems.length)}`, children: [moreLabel, _jsx(ChevronDown, { className: "h-4 w-4 opacity-60", "aria-hidden": "true" })] }) }), _jsx(Popover.Content, { align: "end", className: "flex w-auto max-w-[min(90vw,28rem)] flex-col gap-3", children: overflowItems.map((i) => (_jsxs("div", { className: "flex flex-col gap-1", children: [_jsx("div", { className: "text-xs text-[var(--color-text-muted)]", "aria-hidden": "true", children: i.label }), _jsx("div", { ref: targetRef('overflow', i.id), role: "group", "aria-label": i.label, className: "flex flex-wrap items-center gap-1.5" })] }, `overflow:${i.id}`))) })] })) : null] })) : null }));
}
/* ------------------------------------------------------------------------ */
/* Сторона страницы                                                         */
/* ------------------------------------------------------------------------ */
/** Режим хрома текущего экрана. Без провайдера — `'page'`. */
export function usePageChromeMode() {
    return useContext(ModeContext) ?? 'page';
}
/**
 * Описание экрана — в режиме `'shell'` оболочка покажет его ⓘ-подсказкой у
 * хлебных крошек. Пока страница смонтирована; уход со страницы его снимает.
 * Без провайдера и в режиме `'page'` — тихий no-op (страница рисует описание
 * сама, в `PageHeader`).
 */
export function usePageDescription(description) {
    const setDescription = useContext(SetDescriptionContext);
    const mode = useContext(ModeContext);
    // Владелец записи: уборка старой страницы не должна стереть описание новой.
    const owner = useRef({}).current;
    const value = mode === 'shell' && description != null && description !== '' ? description : null;
    useEffect(() => {
        if (!setDescription || value === null)
            return;
        setDescription(() => ({ owner, value }));
        return () => setDescription((cur) => (cur?.owner === owner ? null : cur));
    }, [setDescription, owner, value]);
}
/**
 * Элементы управления экраном (период, фильтр кабинетов, «Создать»).
 *
 * В режиме `'shell'` под оболочкой они переезжают порталом в навбар
 * ({@link PageToolbarSlot}), сохраняя контекст страницы (QueryClient, роутер,
 * права модуля): состояние и замыкания остаются в дереве страницы. Без
 * провайдера и в режиме `'page'` — строка на месте
 * (`flex flex-wrap justify-end gap-1.5`).
 *
 * Правила:
 * - В навбаре высота 56 px — кнопки и поля `size="sm"` (h-8).
 * - Кнопку отправки формы («Сохранить») сюда НЕ выносить: портал выводит её из
 *   DOM-формы, и submit перестанет работать.
 * - Элемент, переехавший в «Ещё ▾» или обратно, перемонтируется и теряет
 *   локальное состояние (открытый список и т.п.) — держите состояние в URL
 *   или выше по дереву.
 * - Несколько тулбаров одного экрана сливаются в один: элементы идут в
 *   порядке монтирования тулбаров (у `ModuleScreen` его собственный стоит
 *   первым), внутри тулбара — в порядке разметки. Подпись группы — у первого.
 */
function PageToolbarRoot({ 'aria-label': ariaLabel = 'Управление страницей', children, }) {
    const mode = useContext(ModeContext);
    const store = useContext(StoreContext);
    const owner = useRef({}).current;
    const wantsPortal = mode === 'shell' && store !== null;
    useStoreVersion(wantsPortal ? store : null);
    useLayoutEffect(() => {
        if (!wantsPortal || !store)
            return;
        store.pushOwner(owner, ariaLabel);
        return () => store.removeOwner(owner);
    }, [wantsPortal, store, owner, ariaLabel]);
    const portalValue = useMemo(() => (store ? { kind: 'portal', owner, store } : null), [owner, store]);
    // Ключ — ключ ребёнка (`Children.toArray` даёт его и позиционным детям, по
    // исходной позиции): элемент, появившийся раньше уже стоящих, не сдвигает
    // их ключи и не перемонтирует их.
    const ordered = Children.toArray(children).map((child, i) => (_jsx(OrderContext.Provider, { value: i, children: child }, isValidElement(child) && child.key != null ? child.key : i)));
    if (!wantsPortal || !portalValue) {
        return (_jsx("div", { role: "group", "aria-label": ariaLabel, className: "flex flex-wrap items-center justify-end gap-1.5", children: ordered }));
    }
    // Под оболочкой в режиме 'shell' — только порталом, никогда на месте: пока
    // слот не дал цель, элемент рисует `null` (без двойного монтирования и
    // кадра с тулбаром в теле). Элементы неактивного тулбара цели не получат
    // вовсе — слот рисует только активный.
    return _jsx(RenderContext.Provider, { value: portalValue, children: ordered });
}
function PageToolbarItem({ priority = 'primary', label, children }) {
    const render = useContext(RenderContext);
    const order = useContext(OrderContext);
    const id = useId();
    const store = render.kind === 'portal' ? render.store : null;
    const owner = render.kind === 'portal' ? render.owner : null;
    useStoreVersion(store);
    useLayoutEffect(() => {
        if (!store || !owner)
            return;
        store.upsertItem({ id, owner, kind: 'item', priority, label, order });
    }, [store, owner, id, priority, label, order]);
    // Снятие — отдельным эффектом: смена подписи или приоритета не должна
    // снимать элемент и заново его регистрировать (лишний кадр без него).
    useLayoutEffect(() => {
        if (!store)
            return;
        return () => store.removeItem(id);
    }, [store, id]);
    if (!store) {
        return (_jsx("div", { role: "group", "aria-label": label, className: "flex items-center gap-1.5", children: children }));
    }
    const target = store.targetOf(id);
    return target ? createPortal(children, target) : null;
}
function PageToolbarSeparator() {
    const render = useContext(RenderContext);
    const order = useContext(OrderContext);
    const id = useId();
    const store = render.kind === 'portal' ? render.store : null;
    const owner = render.kind === 'portal' ? render.owner : null;
    useLayoutEffect(() => {
        if (!store || !owner)
            return;
        store.upsertItem({ id, owner, kind: 'separator', priority: 'secondary', label: '', order });
        return () => store.removeItem(id);
    }, [store, owner, id, order]);
    if (store)
        return null;
    return _jsx(Separator, { orientation: "vertical", className: "mx-0.5 h-5 self-center" });
}
export const PageToolbar = Object.assign(PageToolbarRoot, {
    Item: PageToolbarItem,
    Separator: PageToolbarSeparator,
});
//# sourceMappingURL=page-chrome.js.map